import { createSlice } from '@reduxjs/toolkit'
import type { PayloadAction } from '@reduxjs/toolkit'
import { resetState } from '../actions'

// Date fields are Firebase Timestamp (live) or {seconds, nanoseconds} (after localStorage)
export interface Project {
    id: string
    workspaceId: string
    ownerId: string
    name: string
    description: unknown
    members?: string[]
    shared_members: string[]
    isPublic?: boolean
    publicShareId?: string | null
    createdAt: any
    updatedAt: any
}

interface ProjectsState {
    byId: Record<string, Project>
    allIds: string[]
    memberProjectIds: string[]
    loading: boolean
    error: string | null
    creatingProjectId: string | null
    invite: {
        loading: boolean
        error: string | null
    }
}

const initialState: ProjectsState = {
    byId: {},
    allIds: [],
    memberProjectIds: [],
    loading: true,
    error: null,
    creatingProjectId: null,
    invite: {
        loading: false,
        error: null
    }
}

const projectsSlice = createSlice({
    name: 'projects',
    initialState,
    reducers: {
        // Saga triggers
        stopListening: () => {},
        createProject: (_state, _action: PayloadAction<{ name: string; workspaceId: string; userId: string }>) => {},
        updateProjectNameSaga: (state, action: PayloadAction<{ projectId: string; name: string }>) => {
            if (state.byId[action.payload.projectId]) {
                state.byId[action.payload.projectId].name = action.payload.name
            }
        },
        updateProjectDescriptionSaga: (state, action: PayloadAction<{ projectId: string; description: string }>) => {
            if (state.byId[action.payload.projectId]) {
                state.byId[action.payload.projectId].description = action.payload.description
            }
        },
        deleteProjectSaga: (_state, _action: PayloadAction<string>) => {},
        publishProjectSaga: (_state, _action: PayloadAction<string>) => {},
        unpublishProjectSaga: (_state, _action: PayloadAction<string>) => {},
        addMemberSaga: (_state, _action: PayloadAction<{ projectId: string; workspaceId: string; userId: string }>) => {},
        removeMemberSaga: (_state, _action: PayloadAction<{ projectId: string; userId: string }>) => {},
        inviteToProjectSaga: (_state, _action: PayloadAction<{ projectId: string; workspaceId: string; email: string; clearInput?: () => void }>) => {},
        updateProjectOwnerSaga: (_state, _action: PayloadAction<{ projectId: string; ownerId: string }>) => {},

        // State mutations
        setProjects: (state, action: PayloadAction<Project[]>) => {
            state.byId = {}
            state.allIds = []
            action.payload.forEach(project => {
                state.byId[project.id] = project
                state.allIds.push(project.id)
            })
        },
        upsertProject: (state, action: PayloadAction<Project>) => {
            const project = action.payload
            if (!state.byId[project.id]) {
                state.allIds.push(project.id)
            }
            state.byId[project.id] = project
        },
        removeProject: (state, action: PayloadAction<string>) => {
            delete state.byId[action.payload]
            state.allIds = state.allIds.filter(id => id !== action.payload)
        },
        setMemberProjectIds: (state, action: PayloadAction<string[]>) => {
            state.memberProjectIds = action.payload
        },
        setLoading: (state, action: PayloadAction<boolean>) => {
            state.loading = action.payload
        },
        setError: (state, action: PayloadAction<string | null>) => {
            state.error = action.payload
        },
        setProjectInviteLoading: (state, action: PayloadAction<boolean>) => {
            state.invite.loading = action.payload
        },
        setProjectInviteError: (state, action: PayloadAction<string | null>) => {
            state.invite.error = action.payload
        },
        setCreatingProjectId: (state, action: PayloadAction<string | null>) => {
            state.creatingProjectId = action.payload
        },
        clearProjects: (state) => {
            state.byId = {}
            state.allIds = []
            state.memberProjectIds = []
            state.loading = true
            state.error = null
            state.creatingProjectId = null
            state.invite = { loading: false, error: null }
        }
    },
    extraReducers: (builder) => {
        builder.addCase(resetState, () => initialState)
    }
})

export const {
    createProject,
    updateProjectNameSaga,
    updateProjectDescriptionSaga,
    deleteProjectSaga,
    publishProjectSaga,
    unpublishProjectSaga,
    addMemberSaga,
    removeMemberSaga,
    inviteToProjectSaga,
    updateProjectOwnerSaga,
    setProjectInviteLoading,
    setProjectInviteError,
    setProjects,
    upsertProject,
    removeProject,
    setMemberProjectIds,
    setLoading,
    setError,
    setCreatingProjectId,
    clearProjects
} = projectsSlice.actions

export default projectsSlice.reducer
